import React, { useState, useRef } from 'react';
import { MessageSquarePlus, Bug, Lightbulb, Calendar, Sparkles, MessageCircle, X, Send, Check, Upload, Image, Link, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const feedbackCategories = [
  { id: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-500', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30' },
  { id: 'feature_request', label: 'Feature Request', icon: Lightbulb, color: 'text-amber-500', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30' },
  { id: 'event_suggestion', label: 'Event Suggestion', icon: Calendar, color: 'text-blue-500', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30' },
  { id: 'experience_suggestion', label: 'Experience Suggestion', icon: Sparkles, color: 'text-purple-500', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30' },
  { id: 'general', label: 'General Feedback', icon: MessageCircle, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/30' },
];

export default function FeedbackButton() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    type: '',
    title: '',
    description: '',
    email: user?.email || '',
    name: user?.full_name || '',
    page_url: '',
    screenshots: [],
    // Bug report fields
    expected_behavior: '',
    actual_behavior: '',
    steps_to_reproduce: '',
    // Feature request fields
    problem_to_solve: '',
    proposed_solution: '',
    // Event/Experience suggestion fields
    suggestion_name: '',
    suggestion_venue: '',
    suggestion_reason: '',
  });

  const handleCategorySelect = (categoryId) => {
    setFormData(prev => ({ 
      ...prev, 
      type: categoryId,
      page_url: window.location.href // Auto-capture current URL
    }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Limit to 3 screenshots
    if (formData.screenshots.length + files.length > 3) {
      toast.error('Maximum 3 screenshots allowed');
      return;
    }

    setIsUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          body: formDataUpload,
        });

        if (!response.ok) throw new Error('Upload failed');
        const data = await response.json();
        return `${API_URL}${data.url}`;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        screenshots: [...prev.screenshots, ...uploadedUrls]
      }));
      toast.success(`${files.length} screenshot(s) uploaded`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload screenshot');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeScreenshot = (index) => {
    setFormData(prev => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.type) {
      toast.error('Please select a feedback category');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    // Type-specific validation
    if (formData.type === 'bug') {
      if (!formData.actual_behavior.trim()) {
        toast.error('Please describe what happened');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const payload = {
        type: formData.type,
        title: formData.title,
        description: formData.description || formData.actual_behavior || formData.problem_to_solve || formData.suggestion_reason || '',
        user_email: formData.email,
        user_name: formData.name || null,
        page_url: formData.page_url || window.location.href,
        browser_info: navigator.userAgent,
        screenshots: formData.screenshots,
      };

      // Add type-specific fields
      if (formData.type === 'bug') {
        payload.expected_behavior = formData.expected_behavior || null;
        payload.actual_behavior = formData.actual_behavior || null;
        payload.steps_to_reproduce = formData.steps_to_reproduce || null;
      } else if (formData.type === 'feature_request') {
        payload.problem_to_solve = formData.problem_to_solve || null;
        payload.proposed_solution = formData.proposed_solution || null;
      } else if (formData.type === 'event_suggestion' || formData.type === 'experience_suggestion') {
        payload.suggestion_name = formData.suggestion_name || null;
        payload.suggestion_venue = formData.suggestion_venue || null;
        payload.suggestion_reason = formData.suggestion_reason || null;
      }

      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        resetForm();
      }, 2000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: '',
      title: '',
      description: '',
      email: user?.email || '',
      name: user?.full_name || '',
      page_url: '',
      screenshots: [],
      expected_behavior: '',
      actual_behavior: '',
      steps_to_reproduce: '',
      problem_to_solve: '',
      proposed_solution: '',
      suggestion_name: '',
      suggestion_venue: '',
      suggestion_reason: '',
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    setSubmitted(false);
    resetForm();
  };

  const selectedCategory = feedbackCategories.find(c => c.id === formData.type);
  const showScreenshotUpload = formData.type === 'bug' || formData.type === 'general';

  return (
    <>
      {/* Floating Feedback Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 group"
        data-testid="feedback-button"
      >
        <div className="relative flex items-center">
          {/* Hover label */}
          <span className="absolute right-full mr-3 px-3 py-1.5 bg-neutral-900 text-white text-sm font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow-lg">
            Send Feedback
          </span>
          {/* Button */}
          <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200">
            <MessageSquarePlus className="w-6 h-6 text-white" />
          </div>
        </div>
      </button>

      {/* Feedback Modal */}
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-xl bg-neutral-900 border-neutral-800 text-white max-h-[90vh] overflow-y-auto">
          {submitted ? (
            // Success State
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
              <p className="text-neutral-400">Your feedback has been submitted successfully.</p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-serif flex items-center gap-2">
                  <MessageSquarePlus className="w-5 h-5 text-amber-400" />
                  Share Your Feedback
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Category Selection */}
                <div className="space-y-2">
                  <Label className="text-neutral-300">What type of feedback?</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {feedbackCategories.map((category) => {
                      const Icon = category.icon;
                      const isSelected = formData.type === category.id;
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleCategorySelect(category.id)}
                          className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-all duration-200 ${
                            isSelected
                              ? `${category.bgColor} ${category.borderColor} border-2`
                              : 'bg-neutral-800/50 border-neutral-700 hover:border-neutral-600'
                          }`}
                          data-testid={`feedback-category-${category.id}`}
                        >
                          <Icon className={`w-5 h-5 mb-1.5 ${isSelected ? category.color : 'text-neutral-400'}`} />
                          <span className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-neutral-400'}`}>
                            {category.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Dynamic Fields Based on Category */}
                {formData.type && (
                  <>
                    {/* Title - Always shown */}
                    <div className="space-y-2">
                      <Label htmlFor="title" className="text-neutral-300">
                        {formData.type === 'bug' ? 'Bug Summary' : 
                         formData.type === 'feature_request' ? 'Feature Name' :
                         formData.type === 'event_suggestion' || formData.type === 'experience_suggestion' ? 'Suggestion Title' :
                         'Title'}
                        <span className="text-red-400 ml-1">*</span>
                      </Label>
                      <Input
                        id="title"
                        placeholder={
                          formData.type === 'bug' ? 'e.g., Button not working on checkout page' :
                          formData.type === 'feature_request' ? 'e.g., Add dark mode option' :
                          formData.type === 'event_suggestion' ? 'e.g., Live Jazz Night at Blue Moon' :
                          formData.type === 'experience_suggestion' ? 'e.g., Wine Tasting Tour' :
                          'Brief summary...'
                        }
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500"
                        data-testid="feedback-title-input"
                      />
                    </div>

                    {/* Bug Report Fields */}
                    {formData.type === 'bug' && (
                      <>
                        <div className="space-y-2">
                          <Label className="text-neutral-300">
                            What happened? <span className="text-red-400">*</span>
                          </Label>
                          <Textarea
                            placeholder="Describe what went wrong..."
                            value={formData.actual_behavior}
                            onChange={(e) => setFormData(prev => ({ ...prev, actual_behavior: e.target.value }))}
                            rows={2}
                            className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500 resize-none"
                            data-testid="feedback-actual-behavior"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-neutral-300">What did you expect to happen?</Label>
                          <Textarea
                            placeholder="Describe the expected behavior..."
                            value={formData.expected_behavior}
                            onChange={(e) => setFormData(prev => ({ ...prev, expected_behavior: e.target.value }))}
                            rows={2}
                            className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500 resize-none"
                            data-testid="feedback-expected-behavior"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-neutral-300">Steps to reproduce (if known)</Label>
                          <Textarea
                            placeholder="1. Go to page...&#10;2. Click on...&#10;3. See error..."
                            value={formData.steps_to_reproduce}
                            onChange={(e) => setFormData(prev => ({ ...prev, steps_to_reproduce: e.target.value }))}
                            rows={3}
                            className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500 resize-none"
                            data-testid="feedback-steps"
                          />
                        </div>
                      </>
                    )}

                    {/* Feature Request Fields */}
                    {formData.type === 'feature_request' && (
                      <>
                        <div className="space-y-2">
                          <Label className="text-neutral-300">What problem would this solve?</Label>
                          <Textarea
                            placeholder="Describe the problem or need this feature would address..."
                            value={formData.problem_to_solve}
                            onChange={(e) => setFormData(prev => ({ ...prev, problem_to_solve: e.target.value }))}
                            rows={2}
                            className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500 resize-none"
                            data-testid="feedback-problem"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-neutral-300">How would you like it to work?</Label>
                          <Textarea
                            placeholder="Describe your ideal solution..."
                            value={formData.proposed_solution}
                            onChange={(e) => setFormData(prev => ({ ...prev, proposed_solution: e.target.value }))}
                            rows={3}
                            className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500 resize-none"
                            data-testid="feedback-solution"
                          />
                        </div>
                      </>
                    )}

                    {/* Event/Experience Suggestion Fields */}
                    {(formData.type === 'event_suggestion' || formData.type === 'experience_suggestion') && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label className="text-neutral-300">
                              {formData.type === 'event_suggestion' ? 'Event Name' : 'Experience Name'}
                            </Label>
                            <Input
                              placeholder={formData.type === 'event_suggestion' ? 'e.g., Jazz Night' : 'e.g., Wine Tasting'}
                              value={formData.suggestion_name}
                              onChange={(e) => setFormData(prev => ({ ...prev, suggestion_name: e.target.value }))}
                              className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500"
                              data-testid="feedback-suggestion-name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-neutral-300">Venue / Location</Label>
                            <Input
                              placeholder="e.g., Blue Moon Brewery"
                              value={formData.suggestion_venue}
                              onChange={(e) => setFormData(prev => ({ ...prev, suggestion_venue: e.target.value }))}
                              className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500"
                              data-testid="feedback-suggestion-venue"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-neutral-300">Why should we add this?</Label>
                          <Textarea
                            placeholder="Tell us why this would be great for EVENT★GOERS..."
                            value={formData.suggestion_reason}
                            onChange={(e) => setFormData(prev => ({ ...prev, suggestion_reason: e.target.value }))}
                            rows={3}
                            className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500 resize-none"
                            data-testid="feedback-suggestion-reason"
                          />
                        </div>
                      </>
                    )}

                    {/* General Feedback Fields */}
                    {formData.type === 'general' && (
                      <div className="space-y-2">
                        <Label className="text-neutral-300">Your Feedback</Label>
                        <Textarea
                          placeholder="Share your thoughts, suggestions, or praise..."
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          rows={4}
                          className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500 resize-none"
                          data-testid="feedback-description-input"
                        />
                      </div>
                    )}

                    {/* Page URL - Editable, auto-captured */}
                    <div className="space-y-2">
                      <Label className="text-neutral-300 flex items-center gap-2">
                        <Link className="w-4 h-4" />
                        Page URL
                      </Label>
                      <Input
                        placeholder="https://..."
                        value={formData.page_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, page_url: e.target.value }))}
                        className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500 text-sm"
                        data-testid="feedback-page-url"
                      />
                      <p className="text-xs text-neutral-500">Auto-captured. Edit if the issue is on a different page.</p>
                    </div>

                    {/* Screenshot Upload - Bug Reports and General Feedback */}
                    {showScreenshotUpload && (
                      <div className="space-y-2">
                        <Label className="text-neutral-300 flex items-center gap-2">
                          <Image className="w-4 h-4" />
                          Screenshots <span className="text-neutral-500 text-xs">(optional, max 3)</span>
                        </Label>
                        
                        {/* Upload Button */}
                        <div className="flex gap-2">
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleFileUpload}
                            className="hidden"
                            data-testid="feedback-screenshot-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading || formData.screenshots.length >= 3}
                            className="border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                          >
                            {isUploading ? (
                              <span className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-neutral-500 border-t-white rounded-full animate-spin" />
                                Uploading...
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <Upload className="w-4 h-4" />
                                Upload Screenshot
                              </span>
                            )}
                          </Button>
                        </div>

                        {/* Screenshot Previews */}
                        {formData.screenshots.length > 0 && (
                          <div className="flex gap-2 flex-wrap mt-2">
                            {formData.screenshots.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Screenshot ${index + 1}`}
                                  className="w-20 h-20 object-cover rounded-lg border border-neutral-700"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeScreenshot(index)}
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-4 h-4 text-white" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Contact Info */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-neutral-300">
                          Email <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500"
                          data-testid="feedback-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-neutral-300">
                          Name <span className="text-neutral-500 text-xs">(optional)</span>
                        </Label>
                        <Input
                          id="name"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-amber-500"
                          data-testid="feedback-name-input"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1 border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                    data-testid="feedback-cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.type}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    data-testid="feedback-submit-button"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Submit Feedback
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
